import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import LeaseholderDetailsModal from '../components/Leaseholders/LeaseholderDetailsModal';
import type { Leaseholder, LeaseholderFilter } from '../types/leaseholder';
import { supabaseLeaseholderService } from '../services/supabase/leaseholders';
import type { SortField, SortDirection } from '../components/Leaseholders/SortControls';
import LeaseholderTable from '../components/Leaseholders/LeaseholderTable';
import LeaseholderFilters from '../components/Leaseholders/LeaseholderFilters';
import ActiveLeaseholderFilters from '../components/Leaseholders/ActiveLeaseholderFilters';
import { useSortedLeaseholders } from '../hooks/useSortedLeaseholders';

export default function Leaseholders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedLeaseholder, setSelectedLeaseholder] = useState<Leaseholder | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<LeaseholderFilter>({
    position: null,
    state: null,
    hasABN: null
  });

  const { data: leaseholders, isLoading, error } = useQuery({
    queryKey: ['leaseholders'],
    queryFn: supabaseLeaseholderService.getAll
  });

  const sortedLeaseholders = useSortedLeaseholders(
    leaseholders,
    sortField,
    sortDirection,
    searchQuery,
    filters
  );

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (leaseholder: Leaseholder) => {
    setSelectedLeaseholder(leaseholder);
  };

  const handleRemoveFilter = (key: keyof LeaseholderFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg">
        <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading Leaseholders</h2>
        <p className="text-red-600">{error instanceof Error ? error.message : 'Failed to load leaseholders'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Leaseholders</h1>
          <div className="w-full sm:w-auto">
            <LeaseholderFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isFilterOpen={isFilterOpen}
              onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
              filters={filters}
              onFilterChange={(newFilters) => setFilters(prev => ({ ...prev, ...newFilters }))}
            />
          </div>
        </div>
        <ActiveLeaseholderFilters filters={filters} onRemoveFilter={handleRemoveFilter} />
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <LeaseholderTable
            leaseholders={sortedLeaseholders || []}
            onViewDetails={handleViewDetails}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        )}
      </div>
      
      {selectedLeaseholder && (
        <LeaseholderDetailsModal
          leaseholder={selectedLeaseholder}
          onClose={() => setSelectedLeaseholder(null)}
        />
      )}
    </div>
  );
}
